import { useNavigate, useParams } from "react-router";
import { Trash2 } from "lucide-react";
import CleButton from "@cle/components/CleButton";
import { eftHashPath } from "@cle/constants/routes";
import ConfirmationModal from "@cle/components/ConfirmationModal";
import RoundedCard from "@cle/components/RoundedCard";
import { useAuthorship, useDeleteAuthorship } from "@/hooks/useAuthorships";
import { formatDanishLongDate } from "@/util/date";
import ReportedStatusBadge from "../../components/ReportedStatusBadge";
import ManualReviewInfoModal from "./components/ManualReviewInfoModal";
import SecretariatMessageCard from "./components/SecretariatMessageCard";
import { formatLessons } from "../../utils/lessonLimits";

const MISSING_VALUE = "-";

const AuthorshipDocumentView = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: authorship,
    isError: isAuthorshipError,
    isLoading: isAuthorshipLoading,
  } = useAuthorship(id);
  const deleteAuthorshipMutation = useDeleteAuthorship();
  const isReported = authorship?.isReported === true;
  const isDeleteDisabled =
    id.length === 0 || deleteAuthorshipMutation.isPending || isReported;

  const handleDeleteAuthorship = (closeConfirmation: () => void) => {
    deleteAuthorshipMutation.mutate(id, {
      onSuccess: () => {
        closeConfirmation();
        void navigate(eftHashPath("authorships"));
      },
    });
  };

  return (
    <div className="cle-course-certificate-document">
      {isAuthorshipError ? (
        <div className="alert alert-warning" role="alert">
          Forfatterskabets oplysninger kunne ikke indlæses lige nu.
        </div>
      ) : null}
      {deleteAuthorshipMutation.isError ? (
        <div className="alert alert-warning" role="alert">
          Forfatterskabet kunne ikke slettes lige nu. Prøv igen.
        </div>
      ) : null}

      <div className="row g-4 align-items-start">
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <RoundedCard
              className="cle-course-certificate-document__side-card"
              header="Forfatterskab"
            >
              <dl className="cle-course-certificate-document__info-list mb-0">
                <dt>Bogtitel</dt>
                <dd>
                  {isAuthorshipLoading
                    ? "Indlæser"
                    : (authorship?.bookTitle ?? MISSING_VALUE)}
                </dd>

                <dt>Forlag</dt>
                <dd>
                  {isAuthorshipLoading
                    ? "Indlæser"
                    : (authorship?.publisher ?? MISSING_VALUE)}
                </dd>

                <dt>Udgivelsesår</dt>
                <dd>
                  {isAuthorshipLoading
                    ? "Indlæser"
                    : (authorship?.publicationYear?.toString() ??
                      MISSING_VALUE)}
                </dd>

                <dt>ISBN</dt>
                <dd>
                  {isAuthorshipLoading
                    ? "Indlæser"
                    : (authorship?.isbn ?? MISSING_VALUE)}
                </dd>
              </dl>
            </RoundedCard>
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <RoundedCard
              className="cle-course-certificate-document__side-card"
              header="Information"
              action={
                <>
                  <ManualReviewInfoModal
                    documentType="forfatterskab"
                    isReported={isReported}
                  />
                  <ConfirmationModal
                    title="Slet forfatterskab"
                    description="Er du sikker på, at du vil slette forfatterskabet? Handlingen kan ikke fortrydes."
                    confirmLabel="Slet"
                    confirmVariant="destructive"
                    confirmButtonId="cle-confirm-delete-authorship"
                    cancelButtonId="cle-cancel-delete-authorship"
                    isConfirming={deleteAuthorshipMutation.isPending}
                    isConfirmDisabled={isDeleteDisabled}
                    onConfirm={({ close }) => handleDeleteAuthorship(close)}
                    trigger={
                      <CleButton
                        id="cle-delete-authorship"
                        variant="destructive"
                        size="sm"
                        disabled={isDeleteDisabled}
                      >
                        <Trash2 size={16} aria-hidden strokeWidth={1.75} />
                        {deleteAuthorshipMutation.isPending
                          ? "Sletter..."
                          : "Slet"}
                      </CleButton>
                    }
                  />
                </>
              }
            >
              <dl className="cle-course-certificate-document__info-list mb-0">
                <dt>Type</dt>
                <dd>Forfatterskab</dd>

                <dt>Status</dt>
                <dd>
                  <ReportedStatusBadge reported={isReported} />
                </dd>

                <dt>Antal lektioner</dt>
                <dd>
                  {isAuthorshipLoading
                    ? "Indlæser"
                    : formatLessons(authorship?.numberOfLessons, MISSING_VALUE)}
                </dd>

                <dt>Oprettet den</dt>
                <dd>
                  {formatDanishLongDate(authorship?.createdAt, MISSING_VALUE)}
                </dd>
              </dl>
            </RoundedCard>
            <SecretariatMessageCard subjectId={id} isReported={isReported} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorshipDocumentView;
